<template>
    <div class="space-y-6">
         <header class="mb-6">
            <Heading
                :level="1"
                title="Usage Overview"
                subtitle="Monitor your organization's usage and quotas."
            />
        </header>

        <SkeletonQuotaUsage  v-if="!loaded" />

        <template v-if="loaded">
            <Card class="rounded">
                <CardContent class="px-6 py-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-2">
                    <p class="text-sm text-muted-foreground">
                        This workspace is currently on <span class="font-semibold">{{ getPlanName }} plan</span>. {{ " " }}
                        <a href="https://streamforge.com/#home-pricing" class="underline">Compare plans</a>
                    </p>

                    <p
                        v-if="currentBillingPeriod"
                        class="text-sm text-muted-foreground md:text-right"
                    >
                        Renews on {{ formatDate(currentBillingPeriod?.ended_at) }}
                    </p>
                </CardContent>
            </Card>

            <Card class="rounded">
                <CardHeader>
                    <CardTitle>Plan Usage Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-8 w-full">
                        <div class="space-y-1">
                            <div class="flex items-center justify-between">
                                <span class="text-sm font-medium">Profile Unlocks</span>
                                <span class="text-sm text-muted-foreground">
                                    {{ $numeral(profileUnlocksUsed).format('0,0') }} /
                                    {{ $numeral(profileUnlocksAllocated).format('0,0') }} included
                                </span>
                            </div>
                            <ProgressBar
                                :model-value="(profileUnlocksUsed / profileUnlocksAllocated) * 100"
                                class="h-2"
                            />
                        </div>
                        <div v-if="hasReports" class="space-y-1">
                            <div class="flex items-center justify-between">
                                <span class="text-sm font-medium">Reports generated</span>
                                <span class="text-sm text-muted-foreground">
                                    {{ $numeral(reportGenerationUsed).format('0,0') }} /
                                    {{ $numeral(reportGenerationAllocated).format('0,0') }} included
                                </span>
                            </div>
                            <ProgressBar
                                :model-value="(reportGenerationUsed / reportGenerationAllocated) * 100"
                                class="h-2"
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card class="rounded">
                <CardHeader>
                    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <CardTitle>Daily Usage</CardTitle>
                        <!-- ShadCN Select Component -->
                        <SelectBox v-model="selectedBillingPeriodKey" @update:model-value="onBillingPeriodChange">
                            <SelectTrigger class="w-[280px]">
                                <SelectValue placeholder="Select a billing period" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem v-for="period in billingPeriods" :key="period.key" :value="period.key">
                                    {{ formatBillingPeriod(period) }}
                                </SelectItem>
                            </SelectContent>
                        </SelectBox>
                    </div>
                </CardHeader>
                <CardContent>
                    <div class="h-[350px]">
                        <Bar :data="chartData" :options="chartOptions" />
                    </div>
                </CardContent>
            </Card>

            <Card class="rounded">
                <CardHeader>
                    <CardTitle>Usage Logs</CardTitle>
                </CardHeader>
                <CardContent>
                    <div class="overflow-x-auto">
                        <ShadCNTable class="text-sm min-w-[600px]">
                        <TableHeader>
                            <TableRow>
                            <TableHead class="px-4">Date</TableHead>
                            <TableHead class="px-4">Type</TableHead>
                            <TableHead class="px-4">User</TableHead>
                            <TableHead class="px-4 text-right">Usage</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            <TableRow v-for="log in logs" :key="log.id">
                            <TableCell>{{ formatDate(log.created_at) }}</TableCell>
                            <TableCell>{{ formatQuotaType(log.usage_type) }}</TableCell>
                            <TableCell>{{ log?.operator?.first_name }} {{ log?.operator?.last_name }}</TableCell>
                            <TableCell class="text-right">{{ log.quota_consumed }}</TableCell>
                            </TableRow>
                        </TableBody>
                        </ShadCNTable>
                    </div>

                    <div v-if="totalPages > 0" class="mt-4 flex items-center justify-between">
                        <Button
                            variant="outline"
                            size="sm"
                            :disabled="currentPage === 1"
                            @click="fetchLogs(currentPage - 1)"
                        >
                            <AppIcon
                                name="chevron-left"
                                class="block sm:hidden h-4 w-4 text-muted-foreground"
                            />
                            <span class="hidden sm:block">Previous Page</span>
                        </Button>

                        <span class="text-sm text-muted-foreground">Page {{ currentPage }} of {{ totalPages }}</span>

                        <Button
                            variant="outline"
                            size="sm"
                            :disabled="currentPage >= totalPages"
                            @click="fetchLogs(currentPage + 1)"
                        >
                            <AppIcon
                                name="chevron-right"
                                class="block sm:hidden h-4 w-4 text-muted-foreground"
                            />
                            <span class="hidden sm:block">Next Page</span>
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </template>

    </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import { Bar } from 'vue-chartjs'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress as ProgressBar } from '@/components/ui/progress'
import { Table as ShadCNTable, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Select as SelectBox, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Heading } from '@/components/ui/typography'
import { Button } from '@/components/ui/button'
import SkeletonQuotaUsage from '@/partials/skeletons/SkeletonQuotaUsage.vue'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

export default {
    name: 'OrganizationUsageSubpage',
    components: {
        Card,
        CardContent,
        CardHeader,
        CardTitle,
        ProgressBar,
        ShadCNTable,
        TableBody,
        TableCell,
        TableHead,
        TableHeader,
        TableRow,
        Bar,
        SelectBox,
        SelectContent,
        SelectItem,
        SelectTrigger,
        SelectValue,
        Heading,
        Button,
        SkeletonQuotaUsage
    },
    props: {
        organization: {
            type: Object,
        },
    },
    data() {
        return {
            quotas: [],
            profileUnlocksUsed: 0,
            profileUnlocksAllocated: 0,
            logs: [],
            chartData: {
                labels: [],
                datasets: [
                    {
                        label: 'Daily Usage',
                        data: [],
                        backgroundColor: '#0ea5e9',
                        borderRadius: 5,
                    },
                ],
            },
            chartOptions: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    y: {
                        beginAtZero: true,
                        ticks: {
                            callback: (value) => Math.round(value),
                        },
                    },
                },
                plugins: {
                    legend: {
                        display: false,
                    },
                },
            },
            billingPeriods: [],
            currentBillingPeriod: null,
            selectedBillingPeriodKey: null,
            currentPage: 1,
            totalPages: 1,
            loaded: false,
        }
    },
    computed: {
        ...mapGetters(['getFromDictionary', 'isSfStaff', 'selfActiveOrganization', 'selfSubscriptionTier', 'checkFeatureEnabled']),
        getPlanName() {
            if (!this.selfSubscriptionTier) {
                return 'Free'
            }
            return this.getFromDictionary(`products.${this.selfSubscriptionTier}.label`) || 'Free'
        },
        hasReports() {
            return this.checkFeatureEnabled('module.reports')
        }
    },

    created() {
        this.getQuotaData(null, 1)
    },

    methods: {
        getQuotaData(billingPeriodKey, page) {
            const params = { page: page || 1 }
            if (billingPeriodKey) params.billingPeriodKey = billingPeriodKey
            this.$axios
                .get(`/organizations/${this.selfActiveOrganization.id}/quota`, { params })
                .then(({ data }) => {
                    const payload = data.payload
                    this.quotas = payload.quotas

                    this.profileUnlocksUsed = payload.quotas.find(q => q.type === 'profile-unlock')?.used_quota || 0
                    this.profileUnlocksAllocated = payload.quotas.find(q => q.type === 'profile-unlock')?.allocated_quota || 0

                    this.reportGenerationUsed = payload.quotas.find(q => q.type === 'report-generation')?.used_quota || 0
                    this.reportGenerationAllocated = payload.quotas.find(q => q.type === 'report-generation')?.allocated_quota || 0

                    this.billingPeriods = payload.billingPeriods
                    if (billingPeriodKey === null) {
                        this.currentBillingPeriod = payload.currentBillingPeriod
                    }
                    this.selectedBillingPeriodKey = payload.currentBillingPeriod.key
                    const operatorsById = _.keyBy(payload.operators, 'operator_id')
                    this.logs = _.map(payload.logs.data, (log) => {
                        log.operator = operatorsById[log.operator_id]?.operator
                        return log
                    })
                    this.currentPage = payload.logs.pagination.page
                    this.totalPages = payload.logs.pagination.totalPages
                    this.chartData = payload.chartData
                    this.loaded = true
                })
                .catch((error) => {
                    console.error('Error fetching quota data', error)
                })
        },
        fetchLogs(page) {
            if (page < 1 || page > this.totalPages) return
            this.getQuotaData(this.selectedBillingPeriodKey, page)
        },
        onBillingPeriodChange() {
            this.getQuotaData(this.selectedBillingPeriodKey, 1)
        },
        formatDate(date) {
            if (!date) return ''
            return new Date(date).toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            })
        },
        formatQuotaType(type) {
            // You can rename them or keep them as-is
            return type
                .split('-')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ')
        },
        formatBillingPeriod(period) {
            if (!period) return ''
            const options = { month: 'short', day: 'numeric', year: 'numeric' }
            const start = new Date(period.started_at).toLocaleDateString('en-US', options)
            const end = new Date(period.ended_at).toLocaleDateString('en-US', options)
            return `${start} - ${end}`
        },
    },
}
</script>
